import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'
import { Confirm } from './Confirm'
import { api, ROLE_LABELS, type Invitation, type Member, type Role } from '../api'

/**
 * This workspace: what it is called, where its site answers, and who can reach it.
 *
 * A workspace used to hold exactly one person, so this is the panel that makes a
 * documentation tool usable by a team. Two things it does that are easy to get
 * wrong:
 *
 * It shows a new invitation's LINK, once, and says so. Only the hash is stored,
 * so this panel is the only place that link will ever exist — a copy button that
 * quietly stops working the second time somebody opens the panel would be worse
 * than no button.
 *
 * It shows what everybody's role means in words. "Editor" and "Viewer" are only
 * obvious to whoever chose the names, and the person picking one is deciding how
 * much of their documentation somebody else can change.
 */

const ROLE_MEANS: Record<Role, string> = {
  owner: 'Everything, including who else is here.',
  admin: 'Write, manage settings, integrations, domains and people below owners.',
  editor: 'Write and propose changes.',
  viewer: 'Read, and join the conversation on a page.',
}

/*
 * The words in the MENUS are `ROLE_LABELS`, shared with the first run's
 * invitation — see `api.ts`. `ROLE_MEANS` above is the longer sentence under
 * the control, and the legend under the roster is the reference.
 */

/**
 * The boxes beside a role, and what ticking one grants.
 *
 * These sentences and the four above them were carried by `title` alone — on a
 * `<select>` whose accessible name is already something else, on a `<label>`
 * that does not pass a title to the box inside it, and on two badges, which
 * are `<span>`s and take no focus at all. A `title` is a mouse affordance: no
 * keyboard reaches it, no touch reveals it, and screen readers are free to
 * ignore it. So on this panel the difference between "may publish to the live
 * site" and "may judge drafts" — the two things this whole screen decides —
 * was legible to a person with a mouse and to nobody else.
 *
 * A row has no space for either sentence and never will. They are reference,
 * so they go where reference goes: once, under the roster, in a disclosure
 * that is open to every input.
 */
const GRANT_MEANS: [string, string][] = [
  ['publish', 'May publish to the live site.'],
  ['review', 'May approve or ask for changes on drafts, without being able to publish.'],
  ['billing', 'May update the card, read invoices and cancel without owning the workspace.'],
]

/** Owner is not offered: it is not something an invitation may grant. */
const INVITABLE: Role[] = ['admin', 'editor', 'viewer']

/** Owner is offered here, unlike in an invitation: transferring is deliberate. */
const ASSIGNABLE: Role[] = ['owner', 'admin', 'editor', 'viewer']
/**
 * What an ADMIN may hand out, which is everything below them.
 *
 * Only an owner can make an owner — the API has always said so, and this list
 * offered it to an admin anyway, so the one choice in the menu that mattered
 * answered "Only an owner can make somebody else an owner." A menu should not
 * contain a refusal.
 */
const ADMIN_MAY_ASSIGN: Role[] = ['admin', 'editor', 'viewer']

export function Members({ onSeePlans }: { onSeePlans: () => void }) {
  const [members, setMembers] = useState<Member[] | null>(null)
  const [invitations, setInvitations] = useState<Invitation[]>([])
  /** This person's standing: in the project on screen, and in the workspace. */
  const [you, setYou] = useState<{ role: Role; workspaceRole?: Role } | null>(null)
  /** Who has a different standing in this project, by user id. */
  const [overrides, setOverrides] = useState<Map<string, Role>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * The member whose "hand this workspace over" question is open.
   *
   * It was a `window.confirm` holding a `\n\n`-formatted paragraph — two
   * sentences the operating system drew in one weight, over the roster it was
   * about, about the least reversible act in the product. Asked in the row
   * now, the way the trash drawer and the publication history ask theirs.
   */
  const [handingTo, setHandingTo] = useState<string | null>(null)
  /** The person a "Remove" press is asking about. See `Confirm`. */
  const [removing, setRemoving] = useState<string | null>(null)

  const [workspace, setWorkspace] = useState<{
    slug: string
    name: string
    /** What the plan includes — the seats are read from here. */
    plan?: { label: string; collaborators: number | null }
    /** Whether signing in to this workspace needs a second step. */
    requireTwoFactor?: boolean
  } | null>(null)
  /** The refusal the switch gives back, said where it was pressed. */
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)
  const [twoFactorBusy, setTwoFactorBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('editor')
  const [invitePublish, setInvitePublish] = useState(false)
  const [inviteReview, setInviteReview] = useState(false)
  const [inviteBilling, setInviteBilling] = useState(false)
  /**
   * The invite field, so a workspace of one can be told what to do about it and
   * then land IN the box rather than beside it. One act, one click.
   */
  const inviteField = useRef<HTMLInputElement | null>(null)
  /** The link, held only until this panel closes. It exists nowhere else. */
  const [fresh, setFresh] = useState<{ email: string; url: string; emailed: boolean } | null>(null)
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    try {
      const [people, config] = await Promise.all([api.members(), api.workspaceSettings()])
      setMembers(people.members)
      setInvitations(people.invitations)
      setYou(people.you)
      setOverrides(new Map(people.projectRoles.map((row) => [row.userId, row.role])))
      setWorkspace(config)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => void load(), [load])

  /**
   * Two questions, because there are two kinds of control on this panel.
   *
   * Who is in the WORKSPACE — inviting somebody, changing their role, taking
   * them out — is a workspace decision, so it reads the workspace role. A
   * project override says what somebody may do in one documentation site; it is
   * not a promotion, and reading it here offered team management to an editor
   * widened to `admin here` and then watched the API refuse it.
   *
   * Their standing in THIS project is the other control, and that one is
   * correctly about this project — the override is what it exists to set.
   */
  /**
   * The seats this plan includes, and how many are gone.
   *
   * `null` where the plan has no limit, which is every paid one: a screen that
   * says "unlimited of unlimited left" is noise.
   */
  const seats = workspace?.plan?.collaborators ?? null
  const taken = (members?.length ?? 0) + invitations.length
  const seatsLeft = seats === null ? null : Math.max(0, seats - taken)

  const inWorkspace = you?.workspaceRole ?? you?.role
  const mayManage = inWorkspace === 'owner' || inWorkspace === 'admin'

  /**
   * How many people here have no second step, for the sentence beside the
   * switch that would send every one of them to an enrolment screen.
   *
   * Null while the roster is still being read, and null on a deployment whose
   * API does not answer the question — a count of zero would say "everybody is
   * covered" about something nobody measured, which is the reading that makes
   * an owner turn the switch on and find out afterwards.
   */
  const withoutSecondStep =
    members === null || members.some((m) => m.hasSecondFactor === undefined)
      ? null
      : members.filter((m) => !m.hasSecondFactor).length

  const setRequireTwoFactor = useCallback(
    async (required: boolean) => {
      setTwoFactorBusy(true)
      setTwoFactorError(null)
      try {
        await api.updateWorkspace({ requireTwoFactor: required })
        await load()
      } catch (e) {
        // The server's own sentence. It says what to do next — set up a second
        // step on your own account first — and rewording it here would be a
        // second, kinder copy of the rule that keeps somebody out.
        setTwoFactorError(e instanceof Error ? e.message : String(e))
      } finally {
        setTwoFactorBusy(false)
      }
    },
    [load],
  )
  const mayManageProject = you?.role === 'owner' || you?.role === 'admin'
  /**
   * Whether the person looking may act on THIS row.
   *
   * Nobody acts upwards: an admin may manage the team and may not touch an
   * owner. The API refuses it — "Only an owner can change an owner" — and a
   * control that is offered and then refuses is worse than one that is not
   * offered, so an owner's row reads as a badge to an admin and as a control to
   * an owner.
   */
  const mayActOn = (theirRole: Role) =>
    mayManage && (inWorkspace === 'owner' || theirRole !== 'owner')
  /** The same rule for the project-role control, asked of the project standing —
   *  and still refusing to act on a workspace owner, which the API also does. */
  const mayNarrow = (theirRole: Role) =>
    mayManageProject && (inWorkspace === 'owner' || theirRole !== 'owner')
  /** The only owner cannot be removed or demoted; the API says so, and offering
   *  it here would be offering a dead end. */
  const owners = (members ?? []).filter((m) => m.role === 'owner').length

  const invite = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.invite({
        email: email.trim(),
        role,
        canPublish: invitePublish,
        canReview: inviteReview,
        canManageBilling: inviteBilling,
      })
      setFresh({ email: email.trim(), url: result.url, emailed: result.emailed })
      setCopied(false)
      setEmail('')
      setInvitePublish(false)
      setInviteReview(false)
      setInviteBilling(false)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const act = async (run: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await run()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * The body, drawn in either frame.
   *
   * People used to be a drawer over whatever screen you were on — a roster,
   * an invite form and a legend, in a 420px column with its own scroll. It is
   * a screen now, in the organisation's own settings, because who is in a
   * workspace is a fact ABOUT the workspace and not a thing you glance at
   * while writing. The drawer stays for the shell's People row until every
   * door has moved, and both draw the same children.
   */
  const body = (
    <>
      {error && <p className="panel__error">{error}</p>}

      {fresh && (
        /* Shown once, and said so. Only the hash is stored, so this panel is
                 the only place this link will ever exist. */
        <section className="drawer__block">
          {/*
                  Two different situations and two different sentences. With a
                  relay the product has already done the sending and the link is
                  a spare; without one the inviter is the delivery, and saying
                  "sent" would be a lie they only discover from somebody who
                  never arrived.
                */}
          <div className="label">
            {fresh.emailed ? `Emailed to ${fresh.email}` : `Send this to ${fresh.email}`}
          </div>
          <p className="drawer__hint">
            {fresh.emailed
              ? 'They have the link. Here it is as well, in case it does not arrive — it is shown once and stored nowhere.'
              : 'This link is shown once. It is not stored anywhere — if you lose it, invite them again.'}
          </p>
          <div className="invitelink">
            <code className="invitelink__url">{fresh.url}</code>
            <button
              className="btn btn--tinted btn--sm"
              onClick={() => {
                void navigator.clipboard
                  .writeText(fresh.url)
                  .then(() => setCopied(true))
                  // Clipboard access can be refused; selecting the text is
                  // still possible, so this is not worth an error.
                  .catch(() => undefined)
              }}
            >
              <Icon name={copied ? 'check' : 'link'} size={13} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </section>
      )}

      {/*
        What the workspace is CALLED lives in the organisation's General
        settings now — see `WorkspaceIdentity`. This screen is about who is
        here, and a rename field on it was the reason nobody could find one on
        the section whose own sentence promises "the workspace itself".
      */}

      {/*
              The seats this plan includes (§55).

              Counted the way the route counts them — people PLUS invitations
              nobody has accepted, because an invitation is a seat somebody is
              about to take. When they are gone the form is not offered: it would
              answer 402 after somebody typed an address, which is the failure the
              plan table exists to stop.

              Nobody is ever removed for this. A workspace that drops to Free with
              eight people keeps all eight and cannot add a ninth.
            */}
      {mayManage && seatsLeft === 0 && (
        <section className="drawer__block" data-seats-full>
          <div className="label">Invite somebody</div>
          <p className="drawer__hint">
            {workspace?.plan?.label ?? 'This plan'} includes {seats}{' '}
            {seats === 1 ? 'person' : 'people'}, and {taken} {taken === 1 ? 'is' : 'are'} here or
            invited. Nobody has been removed. A bigger plan lifts the limit — Settings → Plan.
          </p>
          <button className="btn btn--tinted btn--sm" data-seat-upgrade onClick={onSeePlans}>
            <Icon name="lock" size={13} />
            See plans with more seats
          </button>
        </section>
      )}

      {mayManage && seatsLeft !== 0 && (
        <section className="drawer__block">
          <div className="label">Invite somebody</div>
          <div className="invite">
            {/* A label each. "Invite somebody" is the heading over the row and
                names neither of the two questions in it: where to send the
                invitation, and what it grants. */}
            <label className="field">
              <span className="label">Their email address</span>
              <input
                ref={inviteField}
                className="input"
                type="email"
                autoComplete="off"
                placeholder="their@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && email.trim()) void invite()
                }}
              />
            </label>
            <label className="field">
              <span className="label">What they may do</span>
              <select
                className="select"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {INVITABLE.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn btn--tinted btn--sm"
              disabled={busy || !email.trim()}
              onClick={() => void invite()}
            >
              Invite
            </button>
          </div>
          <div className="people__grantline" aria-label="Extra permissions">
            <label className="people__grant">
              <input
                type="checkbox"
                checked={invitePublish}
                onChange={(e) => setInvitePublish(e.target.checked)}
              />
              publish
            </label>
            <label className="people__grant">
              <input
                type="checkbox"
                checked={inviteReview}
                onChange={(e) => setInviteReview(e.target.checked)}
              />
              review
            </label>
            <label className="people__grant">
              <input
                type="checkbox"
                checked={inviteBilling}
                onChange={(e) => setInviteBilling(e.target.checked)}
              />
              billing
            </label>
          </div>
          <p className="drawer__hint">
            {ROLE_MEANS[role]} Extra permissions are explicit, so a writer, reviewer or finance
            person does not need a broader role.
          </p>
          {seatsLeft !== null && (
            <p className="drawer__hint" data-seats-left>
              {seatsLeft} of {seats} left on {workspace?.plan?.label ?? 'this plan'}, counting
              invitations nobody has accepted yet.
            </p>
          )}
        </section>
      )}

      {mayManage && (
        <section className="drawer__block" data-two-factor-policy>
          <div className="label">Two-step sign-in</div>
          <p className="drawer__hint">
            Ask everybody here for a code from an authenticator as well as a password. Nobody is
            locked out by it: somebody without one is taken to set it up at their next sign-in and
            gets in as soon as they have. Sessions already open are untouched.
          </p>
          {twoFactorError && (
            <p className="screen__error" data-two-factor-error>
              <Icon name="alert" size={13} /> {twoFactorError}
            </p>
          )}
          <label className="settings__row">
            <input
              type="checkbox"
              checked={workspace?.requireTwoFactor ?? false}
              disabled={twoFactorBusy || workspace === null}
              data-require-two-factor
              onChange={(e) => void setRequireTwoFactor(e.target.checked)}
            />
            <span>Require it to sign in to {workspace?.name ?? 'this workspace'}</span>
          </label>
          {/*
            Who it would affect, before it is turned on rather than after.
            A switch that changes what a team must do, beside a list that
            cannot say who is affected, is a decision made blind.
          */}
          <p className="drawer__hint" data-two-factor-count>
            {withoutSecondStep === null
              ? 'Reading who already has one…'
              : withoutSecondStep === 0
                ? 'Everybody here already has a second step.'
                : `${withoutSecondStep} of ${members?.length ?? 0} ${
                    withoutSecondStep === 1 ? 'person has' : 'people have'
                  } no second step yet, and ${
                    withoutSecondStep === 1 ? 'is' : 'are'
                  } marked below. Turning this on sends ${
                    withoutSecondStep === 1 ? 'them' : 'them'
                  } to set one up at their next sign-in.`}
          </p>
        </section>
      )}

      <section className="drawer__block">
        {/* The count, only once it is a count. `members?.length ?? 0` put
                  "0 PEOPLE" over the roster of every workspace for as long as the
                  read took — a heading stating, of a workspace that has at least
                  the person reading it, that nobody is in it. */}
        <div className="label">
          {members === null
            ? 'Who is here'
            : `${members.length} ${members.length === 1 ? 'person' : 'people'}`}
        </div>
        {members === null && (
          <div className="screen__loading" aria-busy="true">
            <p className="sr-only">Loading…</p>
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
        {members?.length === 1 && (
          <div className="drawer__none">
            {/*
                    Two reasons a workspace is one person, and only one of them
                    is fixed by inviting somebody. Telling a workspace whose plan
                    includes one seat to "invite them" is advice that cannot be
                    taken, written by a screen that already knows it — the card
                    directly above this one says the count and the way out.
                  */}
            <p className="drawer__hint">
              {seatsLeft === 0
                ? 'It is just you, and this plan includes the one seat you are in. Nobody can be invited until it includes more.' +
                  // The card is rendered for an owner or an admin only, so
                  // pointing an editor at it would be directions to a place
                  // that is not on their screen.
                  (mayManage ? ' The card above says how to change that.' : '')
                : 'It is just you. Documentation gets better when somebody else can fix what they read — invite them and pick what they may change.'}
            </p>
            {/*
                    Only where it can be honoured, which is two conditions and used
                    to be one. An editor or a viewer cannot invite, so they are
                    told the fact and offered nothing — and a plan with no seat
                    left is the same sentence for a different reason.

                    The button that used to be here regardless was the ONLY thing
                    to press on the first People screen every new workspace sees,
                    and its whole body was `inviteField.current?.focus()`. On Free
                    that field is not rendered at all — one seat, taken by the
                    person reading — so the ref was null and the button did
                    nothing whatever. Not a refusal, which this panel is careful
                    about; silence, which is worse, because a refusal at least
                    says a rule exists.
                  */}
            {mayManage && seatsLeft !== 0 && (
              <button
                className="btn btn--tinted btn--sm"
                onClick={() => {
                  inviteField.current?.focus()
                  inviteField.current?.scrollIntoView({ block: 'center' })
                }}
              >
                <Icon name="people" size={13} />
                Invite somebody
              </button>
            )}
          </div>
        )}
        <ul className="people">
          {(members ?? []).map((m) => (
            <li
              key={m.userId}
              className={`people__row${handingTo === m.userId ? ' people__row--wrap' : ''}`}
            >
              <span className="people__who">
                <span className="people__name truncate">{m.displayName}</span>
                <span className="people__email truncate">{m.email}</span>
                {/*
                  Quiet, and only where the answer is known. A workspace that
                  requires two-step sign-in sends everybody carrying this mark
                  to an enrolment screen at their next sign-in, and an owner
                  deciding whether to require it needs to see who that is.
                */}
                {m.hasSecondFactor === false && (
                  <span
                    className="badge badge--quiet"
                    data-no-second-step
                    data-tip="No second step yet — they would set one up at their next sign-in"
                  >
                    no second step
                  </span>
                )}
              </span>
              {mayActOn(m.role) ? (
                <select
                  className="select select--sm"
                  value={m.role}
                  aria-label={`Role for ${m.displayName}`}
                  disabled={busy}
                  onChange={(e) =>
                    void act(() => api.setMembership(m.userId, { role: e.target.value as Role }))
                  }
                >
                  {(inWorkspace === 'owner' ? ASSIGNABLE : ADMIN_MAY_ASSIGN).map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="badge badge--quiet">{m.role}</span>
              )}
              {/*
                      And their standing in THIS project, when it differs.

                      A workspace holds several sites and the people in it do not
                      have the same standing in each: the person who owns the API
                      reference is a reader of the help centre. The control shows
                      "as the workspace" until somebody chooses otherwise, so the
                      ordinary case reads as one answer rather than two.
                    */}
              {mayNarrow(m.role) ? (
                <select
                  className="select select--sm select--auto"
                  data-project-role={m.userId}
                  value={overrides.get(m.userId) ?? ''}
                  aria-label={`Role in this project for ${m.displayName}`}
                  data-tip="Their role in this project only"
                  disabled={busy}
                  onChange={(e) =>
                    void act(() =>
                      api.setProjectRole(
                        m.userId,
                        e.target.value === '' ? null : (e.target.value as Role),
                      ),
                    )
                  }
                >
                  <option value="">as the workspace</option>
                  {(inWorkspace === 'owner' ? ASSIGNABLE : ADMIN_MAY_ASSIGN).map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}, here only
                    </option>
                  ))}
                </select>
              ) : null}
              {mayActOn(m.role) ? (
                <label className="people__grant">
                  <input
                    type="checkbox"
                    /* Whose box this is. Without it a roster of six is
                             twelve controls named "publish" and "review" and
                             nothing else — a screen reader moving down the
                             column cannot tell whose it has landed on. The
                             visible word leads, so the name still contains the
                             label somebody would say out loud. */
                    aria-label={`publish — ${m.displayName}`}
                    checked={m.canPublish}
                    disabled={busy}
                    onChange={(e) =>
                      void act(() => api.setMembership(m.userId, { canPublish: e.target.checked }))
                    }
                  />
                  publish
                </label>
              ) : null}
              {mayActOn(m.role) ? (
                <label className="people__grant">
                  <input
                    type="checkbox"
                    aria-label={`review — ${m.displayName}`}
                    checked={m.canReview ?? false}
                    disabled={busy}
                    onChange={(e) =>
                      void act(() => api.setMembership(m.userId, { canReview: e.target.checked }))
                    }
                  />
                  review
                </label>
              ) : (
                <>
                  {m.canPublish && <span className="badge badge--publish">publish</span>}
                  {m.canReview && <span className="badge badge--quiet">review</span>}
                </>
              )}
              {m.role === 'owner' ? (
                <span className="badge badge--quiet" data-tip="Owners always manage billing">
                  billing
                </span>
              ) : mayActOn(m.role) ? (
                <label className="people__grant">
                  <input
                    type="checkbox"
                    aria-label={`billing — ${m.displayName}`}
                    checked={m.canManageBilling ?? false}
                    disabled={busy}
                    onChange={(e) =>
                      void act(() =>
                        api.setMembership(m.userId, { canManageBilling: e.target.checked }),
                      )
                    }
                  />
                  billing
                </label>
              ) : m.canManageBilling ? (
                <span className="badge badge--quiet">billing</span>
              ) : null}
              {/*
                      Handing the workspace over (§5).
                    
                      Offered only by an OWNER, only on somebody who is not one,
                      and it says what it costs on the press rather than after it:
                      they decide from then on and the person pressing it becomes
                      an admin. The role menu beside it can already make a second
                      owner; this is the other thing people mean by "it is yours
                      now", and the sole-owner refusal on closing an account has
                      been telling people to do it since before it existed.
                    */}
              {inWorkspace === 'owner' && m.role !== 'owner' && handingTo !== m.userId && (
                <button
                  className="btn btn--outline btn--sm"
                  data-hand-over
                  disabled={busy}
                  onClick={() => setHandingTo(m.userId)}
                >
                  Hand over
                </button>
              )}
              {mayActOn(m.role) && !(m.role === 'owner' && owners <= 1) && (
                <button
                  className="btn btn--danger btn--sm"
                  disabled={busy}
                  onClick={() => setRemoving(m.userId)}
                >
                  Remove
                </button>
              )}
              {/*
                Taking somebody out of a workspace happened on one press, in a
                roster where the button beside it changes a role. It asks now,
                the way every other dangerous act here does.
              */}
              {removing === m.userId && (
                <Confirm
                  data-remove-confirm
                  says={`${m.displayName} loses access to every site in this workspace. What they wrote stays, with their name on it, and you can invite them back.`}
                  does="Remove them"
                  cancel="Keep them"
                  busy={busy}
                  onYes={() => {
                    setRemoving(null)
                    void act(() => api.removeMember(m.userId))
                  }}
                  onNo={() => setRemoving(null)}
                />
              )}
              {handingTo === m.userId && (
                <Confirm
                  data-hand-over-confirm
                  says={`${m.displayName} decides from then on — the plan, the domain, the sites and who is here. You become an admin and keep working exactly as you do now, and only they can hand it back.`}
                  does="Hand it over"
                  cancel="Keep it"
                  busy={busy}
                  onYes={() => {
                    setHandingTo(null)
                    void act(() => api.transferWorkspace(m.userId))
                  }}
                  onNo={() => setHandingTo(null)}
                />
              )}
            </li>
          ))}
        </ul>
        {/* The words the rows have no room for.
                  Shut by default because a roster of one does not need a
                  glossary open above it, and reachable by Tab, by a tap and by
                  a screen reader — which is three more ways than a `title`. */}
        {members !== null && members.length > 0 && (
          <details className="people__legend">
            <summary className="label">What these roles and boxes mean</summary>
            <dl className="people__means">
              {ASSIGNABLE.map((r) => (
                <Fragment key={r}>
                  <dt>{r}</dt>
                  <dd>{ROLE_MEANS[r]}</dd>
                </Fragment>
              ))}
              {GRANT_MEANS.map(([grant, means]) => (
                <Fragment key={grant}>
                  <dt>{grant}</dt>
                  <dd>{means}</dd>
                </Fragment>
              ))}
            </dl>
          </details>
        )}
      </section>

      {invitations.length > 0 && (
        <section className="drawer__block">
          <div className="label">Waiting to be accepted</div>
          <ul className="people">
            {invitations.map((i) => (
              <li key={i.id} className="people__row">
                <span className="people__who">
                  <span className="people__name truncate">{i.email}</span>
                  <span className="people__email">
                    invited as {i.role} · expires{' '}
                    {new Date(i.expiresAt).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                  {(i.canPublish || i.canReview || i.canManageBilling) && (
                    <span className="people__email">
                      {[
                        i.canPublish ? 'publish' : null,
                        i.canReview ? 'review' : null,
                        i.canManageBilling ? 'billing' : null,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  )}
                </span>
                {mayManage && (
                  <button
                    className="btn btn--ghost btn--sm"
                    disabled={busy}
                    onClick={() => void act(() => api.revokeInvitation(i.id))}
                  >
                    Withdraw
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!mayManage && (
        <p className="drawer__hint">
          Only an owner or an admin can invite somebody or remove them.
        </p>
      )}
    </>
  )

  /*
   * One frame, because there is only one left.
   *
   * This was a drawer over whatever somebody was writing, and for a while it
   * was both — a `screen` prop with the old frame kept beside it. Nothing
   * opens the drawer any more: who is in a workspace is a fact ABOUT the
   * workspace, so it is the organisation's People section, beside the plan
   * that decides how many of them there may be.
   */
  return (
    <div className="screen" data-people-screen>
      <header className="screen__head">
        <h1>People</h1>
        <p className="screen__sub">
          Who is in {workspace?.name ?? 'this workspace'}, what they may do, and who has been
          invited.
        </p>
      </header>
      <div className="roster">{body}</div>
    </div>
  )
}
